import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getQuestionById, createQuestion, updateQuestion, getTopicsList, parseOptions } from '../../services/adminApi';
import { toast } from 'react-toastify';

const QuestionForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [topics, setTopics] = useState([]);
  const [isNewTopic, setIsNewTopic] = useState(false);
  
  const [formData, setFormData] = useState({
    question_text: '',
    question_type: 'multiple_choice',
    difficulty: 'medium',
    topic: '',
    options: ['', '', '', ''],
    correct_answer: '',
    correct_option_index: 0,
    explanation: ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Fetch topics
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const data = await getTopicsList();
        setTopics(data.topics || []);
      } catch (error) {
        console.error('Error fetching topics:', error);
      }
    };
    fetchTopics();
  }, []);

  // Fetch question if editing
  useEffect(() => {
    if (isEditMode) {
      fetchQuestion();
    }
  }, [id]);

  const fetchQuestion = async () => {
    setLoading(true);
    try {
      const data = await getQuestionById(id);
      const options = parseOptions(data.options);
      
      // Determine default options based on question type
      let defaultOptions = ['', '', '', ''];
      if (data.question_type === 'true_false') {
        defaultOptions = ['True', 'False'];
      }
      
      setFormData({
        question_text: data.question_text || '',
        question_type: data.question_type || 'multiple_choice',
        difficulty: data.difficulty || 'medium',
        topic: data.topic || '',
        options: options.length > 0 ? options : defaultOptions,
        correct_answer: data.correct_answer || '',
        correct_option_index: data.correct_option_index !== null ? data.correct_option_index : 0,
        explanation: data.explanation || ''
      });
      
      // Check if the topic exists in the topics list
      // If not, enable new topic mode
      if (data.topic && !topics.includes(data.topic)) {
        setIsNewTopic(true);
      }
    } catch (error) {
      toast.error(`Failed to fetch question: ${error.message}`);
      navigate('/admin/questions');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    // Handle question type change
    if (field === 'question_type') {
      if (value === 'true_false') {
        // Set predefined True/False options
        setFormData(prev => ({ 
          ...prev, 
          [field]: value,
          options: ['True', 'False'],
          correct_option_index: 0,
          correct_answer: 'True'
        }));
      } else if (value === 'multiple_choice') {
        // Reset to default multiple choice options
        setFormData(prev => ({ 
          ...prev, 
          [field]: value,
          options: ['', '', '', ''],
          correct_option_index: 0,
          correct_answer: ''
        }));
      } else {
        // For other types (fill_in_blank, short_answer)
        setFormData(prev => ({ 
          ...prev, 
          [field]: value,
          options: [],
          correct_answer: ''
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    
    setTouched(prev => ({ ...prev, [field]: true }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData(prev => ({ ...prev, options: newOptions }));
    setTouched(prev => ({ ...prev, [`option_${index}`]: true }));
  };

  const addOption = () => {
    if (formData.options.length < 6) {
      setFormData(prev => ({
        ...prev,
        options: [...prev.options, '']
      }));
    }
  };

  const removeOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        options: newOptions,
        correct_option_index: prev.correct_option_index >= newOptions.length ? 0 : prev.correct_option_index
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Question text
    if (!formData.question_text.trim()) {
      newErrors.question_text = 'Question text is required';
    } else if (formData.question_text.trim().length < 10) {
      newErrors.question_text = 'Question text must be at least 10 characters (currently ' + formData.question_text.trim().length + ')';
    }

    // Topic
    if (!formData.topic) {
      newErrors.topic = 'Topic is required';
    } else if (formData.topic.trim().length < 3) {
      newErrors.topic = 'Topic must be at least 3 characters';
    }

    // Explanation (now required)
    if (!formData.explanation.trim()) {
      newErrors.explanation = 'Explanation is required';
    } else if (formData.explanation.trim().length < 10) {
      newErrors.explanation = 'Explanation must be at least 10 characters (currently ' + formData.explanation.trim().length + ')';
    }

    // Options validation for multiple choice and true/false
    if (formData.question_type === 'multiple_choice' || formData.question_type === 'true_false') {
      const filledOptions = formData.options.filter(opt => opt.trim());
      
      if (filledOptions.length < 2) {
        newErrors.options = 'At least 2 options are required';
      }
      
      formData.options.forEach((opt, index) => {
        if (!opt.trim()) {
          newErrors[`option_${index}`] = `Option ${index + 1} cannot be empty`;
        }
      });

      // Set correct answer from selected option
      if (formData.correct_option_index !== null && formData.options[formData.correct_option_index]) {
        // This will be set when submitting
      } else {
        newErrors.correct_option_index = 'Please select a correct answer';
      }
    } else {
      // Correct answer for other types (fill_in_blank, short_answer)
      if (!formData.correct_answer.trim()) {
        newErrors.correct_answer = 'Correct answer is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    setTouched({
      question_text: true,
      topic: true,
      explanation: true,
      correct_answer: true,
      ...formData.options.reduce((acc, _, i) => ({ ...acc, [`option_${i}`]: true }), {})
    });

    if (!validateForm()) {
      // Show specific error message for the first error found
      const errorKeys = Object.keys(errors);
      if (errorKeys.length > 0) {
        const firstError = errors[errorKeys[0]];
        toast.error(firstError);
      } else {
        // Validate again to get fresh errors
        if (!formData.question_text.trim()) {
          toast.error('Question text is required');
        } else if (formData.question_text.trim().length < 10) {
          toast.error('Question text must be at least 10 characters (currently ' + formData.question_text.trim().length + ')');
        } else if (!formData.topic) {
          toast.error('Topic is required');
        } else if (!formData.explanation.trim()) {
          toast.error('Explanation is required');
        } else if (formData.explanation.trim().length < 10) {
          toast.error('Explanation must be at least 10 characters (currently ' + formData.explanation.trim().length + ')');
        } else {
          toast.error('Please fix the errors in the form');
        }
      }
      return;
    }

    setSaving(true);

    try {
      const submitData = {
        question_text: formData.question_text.trim(),
        question_type: formData.question_type,
        difficulty: formData.difficulty,
        topic: formData.topic,
        explanation: formData.explanation.trim()
      };

      if (formData.question_type === 'multiple_choice' || formData.question_type === 'true_false') {
        submitData.options = formData.options.filter(opt => opt.trim());
        submitData.correct_answer = formData.options[formData.correct_option_index];
      } else {
        submitData.correct_answer = formData.correct_answer.trim();
      }

      let result;
      if (isEditMode) {
        result = await updateQuestion(id, submitData);
        toast.success('Question updated successfully!');
      } else {
        result = await createQuestion(submitData);
        toast.success('Question created successfully!');
      }

      // Navigate to the question detail page
      const questionId = result.question?.id || result.id;
      navigate(`/admin/questions/${questionId}`);
    } catch (error) {
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} question: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const formatTopic = (topic) => {
    if (!topic) return '';
    return topic.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading question...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <div>
          <Link to="/admin/questions" className="btn btn-link text-decoration-none pl-0">
            <i className="fas fa-arrow-left"></i> Back to Questions
          </Link>
          <h1 className="h3 mb-0 text-gray-800">
            {isEditMode ? `Edit Question #${id}` : 'Create New Question'}
          </h1>
        </div>
        <div>
          <Link
            to="/admin"
            className="btn btn-sm btn-secondary"
          >
            <i className="fas fa-arrow-left mr-1"></i>
            Back to Dashboard
          </Link>
        </div>
      </div>

      {/* Form Card */}
      <form onSubmit={handleSubmit}>
        <div className="card shadow mb-4">
          <div className="card-header py-3">
            <h6 className="m-0 font-weight-bold text-primary">Question Information</h6>
          </div>
          <div className="card-body">
            {/* Question Text */}
            <div className="form-group">
              <label htmlFor="question_text">
                Question Text <span className="text-danger">*</span>
              </label>
              <textarea
                id="question_text"
                className={`form-control ${touched.question_text && errors.question_text ? 'is-invalid' : ''}`}
                rows="4"
                placeholder="Enter your question here..."
                value={formData.question_text}
                onChange={(e) => handleInputChange('question_text', e.target.value)}
                onBlur={() => setTouched(prev => ({ ...prev, question_text: true }))}
              />
              {touched.question_text && errors.question_text && (
                <div className="invalid-feedback d-block">{errors.question_text}</div>
              )}
              <small className="form-text text-muted">
                {formData.question_text.length} characters (minimum 10 required)
              </small>
            </div>

            <div className="row">
              {/* Question Type */}
              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="question_type">
                    Question Type <span className="text-danger">*</span>
                  </label>
                  <select
                    id="question_type"
                    className="form-control"
                    value={formData.question_type}
                    onChange={(e) => handleInputChange('question_type', e.target.value)}
                  >
                    <option value="multiple_choice">Multiple Choice</option>
                    <option value="true_false">True/False</option>
                  </select>
                </div>
              </div>

              {/* Difficulty */}
              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="difficulty">
                    Difficulty Level <span className="text-danger">*</span>
                  </label>
                  <select
                    id="difficulty"
                    className="form-control"
                    value={formData.difficulty}
                    onChange={(e) => handleInputChange('difficulty', e.target.value)}
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>

              {/* Topic */}
              <div className="col-md-4">
                <div className="form-group">
                  <label htmlFor="topic">
                    Topic <span className="text-danger">*</span>
                  </label>
                  
                  {!isNewTopic ? (
                    <>
                      <select
                        id="topic"
                        className={`form-control ${touched.topic && errors.topic ? 'is-invalid' : ''}`}
                        value={formData.topic}
                        onChange={(e) => {
                          if (e.target.value === '__new__') {
                            setIsNewTopic(true);
                            handleInputChange('topic', '');
                          } else {
                            handleInputChange('topic', e.target.value);
                          }
                        }}
                        onBlur={() => setTouched(prev => ({ ...prev, topic: true }))}
                      >
                        <option value="">Select a topic...</option>
                        {topics.map(topic => (
                          <option key={topic} value={topic}>
                            {formatTopic(topic)}
                          </option>
                        ))}
                        <option value="__new__" style={{ fontWeight: 'bold', color: '#28a745' }}>
                          + Add New Topic
                        </option>
                      </select>
                    </>
                  ) : (
                    <>
                      <div className="input-group">
                        <input
                          type="text"
                          id="topic"
                          className={`form-control ${touched.topic && errors.topic ? 'is-invalid' : ''}`}
                          placeholder="Enter new topic name (e.g., advanced_mathematics)"
                          value={formData.topic}
                          onChange={(e) => handleInputChange('topic', e.target.value)}
                          onBlur={() => setTouched(prev => ({ ...prev, topic: true }))}
                        />
                        <div className="input-group-append">
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => {
                              setIsNewTopic(false);
                              handleInputChange('topic', '');
                            }}
                            title="Cancel and select from existing topics"
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      </div>
                      <small className="form-text text-muted">
                        Use lowercase with underscores (e.g., world_history, computer_science)
                      </small>
                    </>
                  )}
                  
                  {touched.topic && errors.topic && (
                    <div className="invalid-feedback d-block">{errors.topic}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Options for Multiple Choice and True/False */}
            {(formData.question_type === 'multiple_choice' || formData.question_type === 'true_false') && (
              <div className="form-group">
                <label>
                  Answer Options <span className="text-danger">*</span>
                  {formData.question_type === 'multiple_choice' && (
                    <span className="text-muted ml-2">(2-6 options)</span>
                  )}
                  {formData.question_type === 'true_false' && (
                    <span className="text-muted ml-2">(Select True or False)</span>
                  )}
                </label>
                {errors.options && (
                  <div className="alert alert-danger py-2">{errors.options}</div>
                )}
                
                {formData.options.map((option, index) => (
                  <div key={index} className="input-group mb-2">
                    <div className="input-group-prepend">
                      <div className="input-group-text">
                        <input
                          type="radio"
                          name="correct_option"
                          checked={formData.correct_option_index === index}
                          onChange={() => handleInputChange('correct_option_index', index)}
                          title="Mark as correct answer"
                        />
                      </div>
                    </div>
                    <input
                      type="text"
                      className={`form-control ${touched[`option_${index}`] && errors[`option_${index}`] ? 'is-invalid' : ''}`}
                      placeholder={`Option ${index + 1}`}
                      value={option}
                      onChange={(e) => handleOptionChange(index, e.target.value)}
                      onBlur={() => setTouched(prev => ({ ...prev, [`option_${index}`]: true }))}
                      disabled={formData.question_type === 'true_false'}
                      readOnly={formData.question_type === 'true_false'}
                      style={formData.question_type === 'true_false' ? { backgroundColor: '#e9ecef', cursor: 'not-allowed' } : {}}
                    />
                    {formData.question_type === 'multiple_choice' && formData.options.length > 2 && (
                      <div className="input-group-append">
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={() => removeOption(index)}
                          title="Remove option"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}
                    {touched[`option_${index}`] && errors[`option_${index}`] && (
                      <div className="invalid-feedback d-block">{errors[`option_${index}`]}</div>
                    )}
                  </div>
                ))}
                
                {formData.question_type === 'multiple_choice' && formData.options.length < 6 && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={addOption}
                  >
                    <i className="fas fa-plus"></i> Add Option
                  </button>
                )}
                
                <small className="form-text text-muted">
                  {formData.question_type === 'true_false' 
                    ? 'Click the radio button next to True or False to select the correct answer'
                    : 'Select the radio button next to the correct answer'}
                </small>
              </div>
            )}



            {/* Explanation */}
            <div className="form-group">
              <label htmlFor="explanation">
                Explanation <span className="text-danger">*</span>
              </label>
              <textarea
                id="explanation"
                className={`form-control ${touched.explanation && errors.explanation ? 'is-invalid' : ''}`}
                rows="4"
                placeholder="Provide an explanation for the correct answer..."
                value={formData.explanation}
                onChange={(e) => handleInputChange('explanation', e.target.value)}
                onBlur={() => setTouched(prev => ({ ...prev, explanation: true }))}
              />
              {touched.explanation && errors.explanation && (
                <div className="invalid-feedback d-block">{errors.explanation}</div>
              )}
              <small className="form-text text-muted">
                {formData.explanation.length} characters (minimum 10 required) - Help users understand why this is the correct answer
              </small>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="d-flex justify-content-between mb-4">
          <Link to="/admin/questions" className="btn btn-secondary">
            <i className="fas fa-times"></i> Cancel
          </Link>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin"></i> {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              <>
                <i className="fas fa-save"></i> {isEditMode ? 'Update Question' : 'Create Question'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default QuestionForm;
